import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";
import Header from "../components/Layout/Header";
import Footer from "../components/Layout/Footer";
import Lottie from "react-lottie";
import animationData from "../Assests/animations/107043-success.json";
import {
  BiCheckCircle,
  BiUser,
  BiPhone,
  BiMapPin,
  BiTime,
  BiCreditCard,
  BiPackage,
} from "react-icons/bi";
import { FiDownload, FiEye, FiExternalLink, FiLoader } from "react-icons/fi";
import { BsArrowRight } from "react-icons/bs";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const OrderSuccessPage = () => {
  return (
    <div>
      <Header />
      <Success />
      <Footer />
    </div>
  );
};

const Success = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Live data fetched via session_id
  const [order, setOrder]           = useState(null);
  const [fetchStatus, setFetchStatus] = useState("loading"); // 'loading' | 'done' | 'error'
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);

  // Fallback: legacy localStorage data (old flow)
  const [legacyData, setLegacyData] = useState(null);

  const defaultOptions = {
    loop: false,
    autoplay: true,
    animationData,
    rendererSettings: { preserveAspectRatio: "xMidYMid slice" },
  };

  useEffect(() => {
    const sessionId = searchParams.get("session_id");

    if (sessionId) {
      // ── Stripe Checkout redirect: look up order by session ──
      let attempts = 0;
      const MAX_ATTEMPTS = 10; // poll ~20 s for webhook to confirm order
      const POLL_MS = 2000;

      const fetchOrder = async () => {
        try {
          const { data } = await axios.get(
            `${server}/payment/order-by-session/${sessionId}`,
            { withCredentials: true }
          );

          if (data.success && data.order) {
            // If webhook already confirmed → done immediately
            if (data.order.paymentInfo?.status === "succeeded") {
              setOrder(data.order);
              setFetchStatus("done");
              // Clear cart from localStorage
              localStorage.setItem("cartItems", JSON.stringify([]));
              localStorage.setItem("latestOrder", JSON.stringify([]));
              return;
            }

            // Payment pending webhook — retry
            attempts += 1;
            if (attempts < MAX_ATTEMPTS) {
              setTimeout(fetchOrder, POLL_MS);
            } else {
              // Webhook is delayed but payment was initiated — show order anyway
              setOrder(data.order);
              setFetchStatus("done");
            }
            return;
          }
          // Order not found yet — retry
          attempts += 1;
          if (attempts < MAX_ATTEMPTS) {
            setTimeout(fetchOrder, POLL_MS);
          } else {
            setFetchStatus("error");
          }
        } catch (err) {
          attempts += 1;
          if (attempts < MAX_ATTEMPTS) {
            setTimeout(fetchOrder, POLL_MS);
          } else {
            setFetchStatus("error");
          }
        }
      };

      fetchOrder();
    } else {
      // ── Legacy localStorage fallback ──
      const raw = localStorage.getItem("latestOrderData");
      if (raw) {
        try {
          setLegacyData(JSON.parse(raw));
          localStorage.removeItem("latestOrderData");
        } catch (_) {}
      }
      setFetchStatus("done");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── helpers ── */
  const formatPrice  = (p) => `$${parseFloat(p || 0).toFixed(2)}`;
  const formatDate   = (d) =>
    new Date(d).toLocaleDateString("en-US", {
      weekday: "long", year: "numeric", month: "long", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  const getOrderNumber = (o) =>
    o?.orderNumber || `#${o?._id?.toString().slice(-8).toUpperCase()}`;

  const handleDownloadReceipt = async (orderId) => {
    setDownloadingReceipt(true);
    try {
      const response = await axios.get(`${server}/payment/order/${orderId}/receipt`, {
        withCredentials: true,
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `Receipt-${orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Receipt downloaded!");
    } catch (err) {
      toast.error("Failed to download receipt. Please try again.");
    } finally {
      setDownloadingReceipt(false);
    }
  };

  /* ────────────── RENDER: LOADING ────────────── */
  if (fetchStatus === "loading") {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <AiOutlineLoading3Quarters className="animate-spin text-5xl text-indigo-500" />
        <p className="text-gray-600 text-lg font-medium">Confirming your order…</p>
        <p className="text-gray-400 text-sm">
          Waiting for payment confirmation. This usually takes a few seconds.
        </p>
      </div>
    );
  }

  /* ────────────── RENDER: STRIPE ORDER ────────────── */
  if (order) {
    const isPaid = order.paymentInfo?.status === "succeeded";

    return (
      <div className="min-h-screen bg-gray-50 py-10 px-4 font-sans">
        <div className="max-w-3xl mx-auto">

          {/* Hero */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 sm:p-12 mb-8 text-center">
            <Lottie options={defaultOptions} width={150} height={150} />

            <h1 className="text-3xl font-bold text-gray-900 mb-2 mt-4 tracking-tight">
              Order Confirmed!
            </h1>

            <div className="inline-block bg-gray-50 text-gray-800 px-6 py-4 rounded-xl border border-gray-200 mb-6">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1 font-semibold">Order ID</p>
              <p className="text-xl sm:text-2xl font-bold tracking-widest">{getOrderNumber(order)}</p>
            </div>

            <div className="flex items-center justify-center gap-2 mb-3">
              <BiCheckCircle className="text-green-500 text-2xl" />
              <span className="text-gray-700 font-semibold">
                {isPaid ? "Payment Verified via Stripe" : "Payment Processing…"}
              </span>
            </div>

            {order.user?.email && (
              <div className="bg-green-50 border border-green-200 rounded-lg py-2 px-4 text-sm text-green-800 mb-4">
                📧 Confirmation email sent to <strong>{order.user?.email || "you"}</strong>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
              {/* Download PDF receipt */}
              {isPaid && (
                <button
                  onClick={() => handleDownloadReceipt(order._id)}
                  disabled={downloadingReceipt}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition font-medium disabled:opacity-70"
                >
                  {downloadingReceipt ? (
                    <><FiLoader className="animate-spin" /> Downloading…</>
                  ) : (
                    <><FiDownload /> Download Invoice</>
                  )}
                </button>
              )}

              {/* View Stripe hosted receipt */}
              {order.stripeReceiptUrl && (
                <a
                  href={order.stripeReceiptUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-full hover:bg-black transition font-medium"
                >
                  <FiExternalLink /> Stripe Receipt
                </a>
              )}
            </div>
          </div>

          {/* Order Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">

            {/* Summary */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <BiPackage className="text-blue-600" /> Order Summary
              </h2>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Status</span>
                  <span className={`font-semibold ${isPaid ? "text-green-600" : "text-amber-600"}`}>
                    {order.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Paid</span>
                  <span className="text-xl font-bold text-green-600">
                    {formatPrice(order.totalPrice)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="font-medium">{formatPrice(order.subTotalPrice)}</span>
                </div>
                {order.shippingPrice > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Shipping</span>
                    <span className="font-medium">{formatPrice(order.shippingPrice)}</span>
                  </div>
                )}
                {order.discountPrice > 0 && (
                  <div className="flex justify-between text-emerald-600">
                    <span>Discount</span>
                    <span className="font-medium">-{formatPrice(order.discountPrice)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-1">
                  <span className="text-gray-500">Payment</span>
                  <div className="flex items-center gap-1">
                    <BiCreditCard className="text-blue-600" />
                    <span className="font-medium">{order.paymentInfo?.type || "Stripe"}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Date</span>
                  <div className="flex items-center gap-1 text-xs text-right">
                    <BiTime className="text-blue-600 flex-shrink-0" />
                    <span>{formatDate(order.createdAt)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Shipping */}
            {order.shippingAddress && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <BiMapPin className="text-blue-600" /> Shipping Address
                </h2>
                <div className="text-sm text-gray-700 space-y-1">
                  <p className="font-medium">{order.shippingAddress.fullName || order.shippingAddress.address1}</p>
                  {order.shippingAddress.address1 && (
                    <p>{order.shippingAddress.address1}</p>
                  )}
                  {order.shippingAddress.address2 && (
                    <p>{order.shippingAddress.address2}</p>
                  )}
                  <p>
                    {order.shippingAddress.city}, {order.shippingAddress.country}{" "}
                    {order.shippingAddress.zipCode}
                  </p>
                  {order.shippingAddress.phoneNumber && (
                    <p className="flex items-center gap-1 mt-1">
                      <BiPhone /> {order.shippingAddress.phoneNumber}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Items */}
          {order.cart?.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <BiPackage className="text-blue-600" /> Order Items
              </h2>
              <div className="space-y-3">
                {order.cart.map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 border border-gray-100 rounded-lg">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <BiPackage className="text-gray-400 text-xl" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{item.name}</p>
                      <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                    </div>
                    <p className="text-sm font-bold text-gray-800 flex-shrink-0">
                      {formatPrice((item.discountedPrice || item.price || 0) * item.quantity)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer actions */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/profile"
              className="flex items-center justify-center gap-2 bg-blue-600 text-white px-8 py-3 rounded-full hover:bg-blue-700 transition font-medium"
            >
              <FiEye /> Track Orders
            </Link>
            <Link
              to="/"
              className="flex items-center justify-center gap-2 bg-gray-900 text-white px-8 py-3 rounded-full hover:bg-black transition font-medium"
            >
              Continue Shopping <BsArrowRight />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  /* ────────────── RENDER: LEGACY fallback ────────────── */
  if (legacyData) {
    return (
      <div className="min-h-screen bg-gray-50 py-10 px-4 font-sans">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center">
            <Lottie options={defaultOptions} width={150} height={150} />
            <h1 className="text-3xl font-bold text-gray-900 mb-2 mt-4 tracking-tight">Order Placed!</h1>
            <p className="text-gray-600 mb-8">Your order has been placed successfully.</p>
            <Link
              to="/profile"
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-3 rounded-full hover:bg-blue-700 transition font-medium"
            >
              View My Orders <BsArrowRight />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  /* ────────────── RENDER: Generic success or Error ────────────── */
  const isError = fetchStatus === "error";

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 font-sans flex items-center justify-center">
      <div className="max-w-xl w-full mx-auto">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center">
          {isError ? (
            <div className="w-24 h-24 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-6">
              <span className="text-5xl text-red-500">⚠️</span>
            </div>
          ) : (
            <Lottie options={defaultOptions} width={150} height={150} />
          )}
          
          <h1 className={`text-3xl font-bold mb-3 tracking-tight ${isError ? 'text-red-600' : 'text-gray-900'}`}>
            {isError ? "Payment Status Unknown" : "Order Placed!"}
          </h1>
          
          <p className="text-gray-600 mb-8 text-lg">
            {isError
              ? "We couldn't confirm your order details right now, but your payment may have been processed. Please check your email for a receipt or contact support if you need help."
              : "Your order has been placed successfully. Thank you for your purchase!"}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {isError ? (
              <Link
                to="/profile"
                className="inline-flex items-center justify-center gap-2 bg-gray-900 text-white px-8 py-3 rounded-full hover:bg-black transition font-medium w-full sm:w-auto"
              >
                View My Orders
              </Link>
            ) : (
              <>
                <Link
                  to="/profile"
                  className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-8 py-3 rounded-full hover:bg-blue-700 transition font-medium w-full sm:w-auto"
                >
                  Track Order
                </Link>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 bg-gray-100 text-gray-800 px-8 py-3 rounded-full hover:bg-gray-200 transition font-medium w-full sm:w-auto"
                >
                  Continue Shopping
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccessPage;
